// ============================================================================
// CHANGO V5.0 — Public marketing site shell
// ============================================================================
// Wraps public marketing pages with a lightweight header + footer so the
// official website keeps a consistent chrome across static and CMS pages.
//
// Header: logo + top-level nav + login button.
// Footer: product links + copyright + ICP record.
// ============================================================================

import React from 'react';
import { Link, NavLink } from 'react-router-dom';

// Cross-SPA URL helper (Task #55). `appUrl` returns a relative path in dev
// (single Vite origin) and an absolute https://app.chango-tech.com URL in prod.
import { appUrl } from '../../lib/hosts.js';

const NAV_ITEMS = [
  { to: '/',         label: '首页'   },
  { to: '/product',  label: '产品'   },
  { to: '/pricing',  label: '定价'   },
  { to: '/about',    label: '关于'   }
];

function SiteHeader() {
  return (
    <header className="sticky top-0 z-20 bg-white/80 backdrop-blur border-b border-slate-200">
      <div className="max-w-6xl mx-auto px-6 h-14 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 font-semibold text-slate-800">
          <span className="inline-block h-7 w-7 rounded bg-chango-600 text-white grid place-items-center text-xs">
            长
          </span>
          <span>长戈 CHANGO</span>
        </Link>
        <nav className="hidden md:flex items-center gap-5 text-sm">
          {NAV_ITEMS.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === '/'}
              className={({ isActive }) => [
                'text-slate-600 hover:text-slate-900 transition-colors',
                isActive ? 'text-chango-700 font-semibold' : ''
              ].join(' ')}
            >
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="flex items-center gap-2">
          {/* Cross-SPA: on prod these land on app.chango-tech.com, where /login and
              /register actually exist. `appUrl` returns a bare path in dev so
              the single-origin Vite server still works. A plain <a> is used
              instead of <Link> because <Link> only supports in-router routes
              and we don't want to accidentally intercept the cross-host jump. */}
          <a
            href={appUrl('/login')}
            className="text-sm text-slate-600 hover:text-slate-900"
          >
            登录
          </a>
          <a
            href={appUrl('/login')}
            className="rounded-md bg-chango-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-chango-700"
          >
            进入应用
          </a>
        </div>
      </div>
    </header>
  );
}

function SiteFooter() {
  return (
    <footer className="mt-12 border-t border-slate-200 bg-slate-50">
      <div className="max-w-6xl mx-auto px-6 py-10 grid grid-cols-2 sm:grid-cols-4 gap-8 text-sm">
        <div>
          <div className="font-semibold text-slate-800 mb-2">产品</div>
          <ul className="space-y-1 text-slate-600">
            <li><Link to="/product" className="hover:text-slate-900">长戈出口决策云</Link></li>
            <li><Link to="/product" className="hover:text-slate-900">产品体系</Link></li>
            <li><Link to="/pricing" className="hover:text-slate-900">版本与定价</Link></li>
          </ul>
        </div>
        <div>
          <div className="font-semibold text-slate-800 mb-2">公司</div>
          <ul className="space-y-1 text-slate-600">
            <li><Link to="/about"   className="hover:text-slate-900">关于我们</Link></li>
            <li><Link to="/" className="hover:text-slate-900">长戈出口云</Link></li>
          </ul>
        </div>
        <div>
          <div className="font-semibold text-slate-800 mb-2">服务</div>
          <ul className="space-y-1 text-slate-600">
            <li><a href={appUrl('/login')} className="hover:text-slate-900">进入应用</a></li>
            <li><a href={appUrl('/register')} className="hover:text-slate-900">开通试用</a></li>
            <li><Link to="/pricing" className="hover:text-slate-900">实施服务</Link></li>
          </ul>
        </div>
        <div>
          <div className="font-semibold text-slate-800 mb-2">官网</div>
          <ul className="space-y-1 text-slate-600">
            <li><a href="https://chango-tech.com" className="hover:text-slate-900">chango-tech.com</a></li>
            <li><a href="https://beian.miit.gov.cn/" target="_blank" rel="noreferrer noopener" className="hover:text-slate-900">ICP备案查询</a></li>
          </ul>
        </div>
      </div>
      <div className="border-t border-slate-200">
        <div className="max-w-6xl mx-auto px-6 py-4 text-xs text-slate-500 flex flex-wrap justify-between gap-2">
          <span>© 2026 长戈 CHANGO · 出口云</span>
          <a
            href="https://beian.miit.gov.cn/"
            target="_blank"
            rel="noreferrer noopener"
            className="hover:text-slate-700"
          >
            浙ICP备2024126786号-1
          </a>
        </div>
      </div>
    </footer>
  );
}

export default function PublicSiteShell({ children }) {
  return (
    <div className="min-h-screen flex flex-col bg-white">
      <SiteHeader />
      <main className="flex-1">
        {children}
      </main>
      <SiteFooter />
    </div>
  );
}
